import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type {
  SafeToSpendData,
  RunwayData,
  NetWorthData,
  CalendarForecastItem,
  AnomalyItem,
  DebtPayoffPlan,
  SpendingTrend,
  TaxSummaryData,
  AnalyticsOverviewData,
  SpendingTrendMonth,
  BudgetPerformanceMonth,
  SpendingVelocityData,
  CategoryTreemapData,
  FinancialHealthData,
  ExpenseCalendarData,
  TopMerchant,
  RecurringVsDiscretionaryMonth,
  DayOfWeekData,
  ItemsSummaryCategory,
  ItemsFrequencyItem,
  ItemsSearchResult,
  ProjectedBalanceData,
} from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface AnalyticsState {
  safeToSpend: SafeToSpendData | null;
  runway: RunwayData | null;
  netWorth: NetWorthData | null;
  calendarForecast: CalendarForecastItem[];
  anomalies: AnomalyItem[];
  debtPayoff: DebtPayoffPlan | null;
  spendingTrends: SpendingTrend[];
  taxSummary: TaxSummaryData | null;
  detectedSubscriptions: unknown[];
  overview: AnalyticsOverviewData | null;
  spendingTrendMonths: SpendingTrendMonth[];
  budgetPerformance: BudgetPerformanceMonth[];
  spendingVelocity: SpendingVelocityData | null;
  categoryTreemap: CategoryTreemapData | null;
  financialHealth: FinancialHealthData | null;
  expenseCalendar: ExpenseCalendarData | null;
  topMerchants: TopMerchant[];
  recurringVsDiscretionary: RecurringVsDiscretionaryMonth[];
  dayOfWeek: DayOfWeekData[];
  // T114-F4: Items analytics
  itemsSummary: ItemsSummaryCategory[];
  itemsFrequency: ItemsFrequencyItem[];
  itemsSearch: ItemsSearchResult[];
  isItemsSummaryLoading: boolean;
  isItemsFrequencyLoading: boolean;
  isItemsSearchLoading: boolean;
  // T155P2: Cash-Flow Foresight
  projectedBalance: ProjectedBalanceData | null;
  isProjectedLoading: boolean;
  isLoading: boolean;
  isOverviewLoading: boolean;
  isTrendsLoading: boolean;
  isBudgetPerfLoading: boolean;
  isVelocityLoading: boolean;
  isTreemapLoading: boolean;
  isHealthLoading: boolean;
  isCalendarLoading: boolean;
  isMerchantsLoading: boolean;
  isRecurringLoading: boolean;
  isDayOfWeekLoading: boolean;
  error: string | null;
}

const initialState: AnalyticsState = {
  safeToSpend: null,
  runway: null,
  netWorth: null,
  calendarForecast: [],
  anomalies: [],
  debtPayoff: null,
  spendingTrends: [],
  taxSummary: null,
  detectedSubscriptions: [],
  overview: null,
  spendingTrendMonths: [],
  budgetPerformance: [],
  spendingVelocity: null,
  categoryTreemap: null,
  financialHealth: null,
  expenseCalendar: null,
  topMerchants: [],
  recurringVsDiscretionary: [],
  dayOfWeek: [],
  itemsSummary: [],
  itemsFrequency: [],
  itemsSearch: [],
  isItemsSummaryLoading: false,
  isItemsFrequencyLoading: false,
  isItemsSearchLoading: false,
  // T155P2
  projectedBalance: null,
  isProjectedLoading: false,
  isLoading: false,
  isOverviewLoading: false,
  isTrendsLoading: false,
  isBudgetPerfLoading: false,
  isVelocityLoading: false,
  isTreemapLoading: false,
  isHealthLoading: false,
  isCalendarLoading: false,
  isMerchantsLoading: false,
  isRecurringLoading: false,
  isDayOfWeekLoading: false,
  error: null,
};

export const fetchSafeToSpend = createAsyncThunk(
  'analytics/fetchSafeToSpend',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.safeToSpend);
      return (response.data.data || response.data) as SafeToSpendData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch safe-to-spend'));
    }
  }
);

export const fetchRunway = createAsyncThunk(
  'analytics/fetchRunway',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.runway);
      return (response.data.data || response.data) as RunwayData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch runway'));
    }
  }
);

export const fetchNetWorth = createAsyncThunk(
  'analytics/fetchNetWorth',
  async (params: { startDate?: string; endDate?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.netWorth, { params });
      return (response.data.data || response.data) as NetWorthData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch net worth'));
    }
  }
);

export const takeSnapshot = createAsyncThunk(
  'analytics/takeSnapshot',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.analytics.snapshot);
      return response.data.data || response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to take snapshot'));
    }
  }
);

export const detectSubscriptions = createAsyncThunk(
  'analytics/detectSubscriptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.subscriptions);
      return response.data.data || response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to detect subscriptions'));
    }
  }
);

export const fetchCalendarForecast = createAsyncThunk(
  'analytics/fetchCalendarForecast',
  async (params: { startDate?: string; endDate?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.calendar, { params });
      return (response.data.data || response.data) as CalendarForecastItem[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch calendar forecast'));
    }
  }
);

export const fetchAnomalies = createAsyncThunk(
  'analytics/fetchAnomalies',
  async (params: { startDate?: string; endDate?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.anomalies, { params });
      return (response.data.data || response.data) as AnomalyItem[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to detect anomalies'));
    }
  }
);

export const fetchDebtPayoff = createAsyncThunk(
  'analytics/fetchDebtPayoff',
  async (params: { strategy?: 'avalanche' | 'snowball'; extraPayment?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.debtPayoff, { params });
      return (response.data.data || response.data) as DebtPayoffPlan;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch debt payoff plan'));
    }
  }
);

export const fetchSpendingTrends = createAsyncThunk(
  'analytics/fetchSpendingTrends',
  async (params: { startDate?: string; endDate?: string; groupBy?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.spendingTrends, { params });
      return (response.data.data || response.data) as SpendingTrend[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch spending trends'));
    }
  }
);

export const fetchTaxSummary = createAsyncThunk(
  'analytics/fetchTaxSummary',
  async (params: { startDate?: string; endDate?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.taxSummary, { params });
      return (response.data.data || response.data) as TaxSummaryData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch tax summary'));
    }
  }
);

export const fetchOverview = createAsyncThunk(
  'analytics/fetchOverview',
  async (params: { startDate?: string; endDate?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.overview, { params });
      return (response.data.data || response.data) as AnalyticsOverviewData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch analytics overview'));
    }
  }
);

export const fetchSpendingTrendMonths = createAsyncThunk(
  'analytics/fetchSpendingTrendMonths',
  async (params: { months?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.spendingTrends, { params });
      return (response.data.data || response.data) as SpendingTrendMonth[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch spending trends'));
    }
  }
);

export const fetchBudgetPerformance = createAsyncThunk(
  'analytics/fetchBudgetPerformance',
  async (params: { months?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.budgetPerformance, { params });
      return (response.data.data || response.data) as BudgetPerformanceMonth[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch budget performance'));
    }
  }
);

export const fetchSpendingVelocity = createAsyncThunk(
  'analytics/fetchSpendingVelocity',
  async (params: { months?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.spendingVelocity, { params });
      return (response.data.data || response.data) as SpendingVelocityData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch spending velocity'));
    }
  }
);

export const fetchCategoryTreemap = createAsyncThunk(
  'analytics/fetchCategoryTreemap',
  async (params: { startDate?: string; endDate?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.categoryTreemap, { params });
      return (response.data.data || response.data) as CategoryTreemapData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch category treemap'));
    }
  }
);

export const fetchFinancialHealth = createAsyncThunk(
  'analytics/fetchFinancialHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.financialHealth);
      return (response.data.data || response.data) as FinancialHealthData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch financial health'));
    }
  }
);

export const fetchExpenseCalendar = createAsyncThunk(
  'analytics/fetchExpenseCalendar',
  async (params: { months?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.expenseCalendar, { params });
      return (response.data.data || response.data) as ExpenseCalendarData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch expense calendar'));
    }
  }
);

export const fetchTopMerchants = createAsyncThunk(
  'analytics/fetchTopMerchants',
  async (params: { startDate?: string; endDate?: string; limit?: number; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.topMerchants, { params });
      return (response.data.data || response.data) as TopMerchant[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch top merchants'));
    }
  }
);

export const fetchRecurringVsDiscretionary = createAsyncThunk(
  'analytics/fetchRecurringVsDiscretionary',
  async (params: { months?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.recurringVsDiscretionary, { params });
      return (response.data.data || response.data) as RecurringVsDiscretionaryMonth[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch recurring vs discretionary'));
    }
  }
);

export const fetchDayOfWeek = createAsyncThunk(
  'analytics/fetchDayOfWeek',
  async (params: { startDate?: string; endDate?: string; timezone?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.dayOfWeek, { params });
      return (response.data.data || response.data) as DayOfWeekData[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch day of week data'));
    }
  }
);

// [T114-F4] Items analytics thunks
export const fetchItemsSummary = createAsyncThunk(
  'analytics/fetchItemsSummary',
  async (params: { dateFrom?: string; dateTo?: string; timezone?: string; accountId?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactions.itemsSummary, { params });
      return (response.data.data || response.data) as ItemsSummaryCategory[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch items summary'));
    }
  }
);

export const fetchItemsFrequency = createAsyncThunk(
  'analytics/fetchItemsFrequency',
  async (params: { dateFrom?: string; dateTo?: string; timezone?: string; limit?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactions.itemsFrequency, { params });
      return (response.data.data || response.data) as ItemsFrequencyItem[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch items frequency'));
    }
  }
);

export const fetchItemsSearch = createAsyncThunk(
  'analytics/fetchItemsSearch',
  async (params: { q: string; dateFrom?: string; dateTo?: string; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactions.itemsSearch, { params });
      return (response.data.data || response.data) as ItemsSearchResult[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to search items'));
    }
  }
);

// [T155P2] Cash-Flow Foresight thunk
export const fetchProjectedBalance = createAsyncThunk(
  'analytics/fetchProjectedBalance',
  async (params: { days: 30 | 60 | 90 }, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.analytics.projectedBalance, { params });
      return (response.data.data || response.data) as ProjectedBalanceData;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch projected balance'));
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    clearAnalyticsError: (state) => {
      state.error = null;
    },
    clearAnalytics: () => initialState,
  },
  extraReducers: (builder) => {
    // Safe to Spend
    builder.addCase(fetchSafeToSpend.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchSafeToSpend.fulfilled, (state, action) => { state.isLoading = false; state.safeToSpend = action.payload; });
    builder.addCase(fetchSafeToSpend.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Runway
    builder.addCase(fetchRunway.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchRunway.fulfilled, (state, action) => { state.isLoading = false; state.runway = action.payload; });
    builder.addCase(fetchRunway.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Net Worth
    builder.addCase(fetchNetWorth.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchNetWorth.fulfilled, (state, action) => { state.isLoading = false; state.netWorth = action.payload; });
    builder.addCase(fetchNetWorth.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Snapshot
    builder.addCase(takeSnapshot.pending, (state) => { state.isLoading = true; });
    builder.addCase(takeSnapshot.fulfilled, (state) => { state.isLoading = false; });
    builder.addCase(takeSnapshot.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Detected Subscriptions
    builder.addCase(detectSubscriptions.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(detectSubscriptions.fulfilled, (state, action) => { state.isLoading = false; state.detectedSubscriptions = action.payload; });
    builder.addCase(detectSubscriptions.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Calendar Forecast
    builder.addCase(fetchCalendarForecast.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchCalendarForecast.fulfilled, (state, action) => { state.isLoading = false; state.calendarForecast = action.payload; });
    builder.addCase(fetchCalendarForecast.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Anomalies
    builder.addCase(fetchAnomalies.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchAnomalies.fulfilled, (state, action) => { state.isLoading = false; state.anomalies = action.payload; });
    builder.addCase(fetchAnomalies.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Debt Payoff
    builder.addCase(fetchDebtPayoff.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchDebtPayoff.fulfilled, (state, action) => { state.isLoading = false; state.debtPayoff = action.payload; });
    builder.addCase(fetchDebtPayoff.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Spending Trends
    builder.addCase(fetchSpendingTrends.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchSpendingTrends.fulfilled, (state, action) => { state.isLoading = false; state.spendingTrends = action.payload; });
    builder.addCase(fetchSpendingTrends.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Tax Summary
    builder.addCase(fetchTaxSummary.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchTaxSummary.fulfilled, (state, action) => { state.isLoading = false; state.taxSummary = action.payload; });
    builder.addCase(fetchTaxSummary.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Overview (T99P1)
    builder.addCase(fetchOverview.pending, (state) => { state.isOverviewLoading = true; state.error = null; });
    builder.addCase(fetchOverview.fulfilled, (state, action) => { state.isOverviewLoading = false; state.overview = action.payload; });
    builder.addCase(fetchOverview.rejected, (state, action) => { state.isOverviewLoading = false; state.error = action.payload as string; });

    // Spending Trend Months (T99P1)
    builder.addCase(fetchSpendingTrendMonths.pending, (state) => { state.isTrendsLoading = true; state.error = null; });
    builder.addCase(fetchSpendingTrendMonths.fulfilled, (state, action) => { state.isTrendsLoading = false; state.spendingTrendMonths = action.payload; });
    builder.addCase(fetchSpendingTrendMonths.rejected, (state, action) => { state.isTrendsLoading = false; state.error = action.payload as string; });

    // Budget Performance
    builder.addCase(fetchBudgetPerformance.pending, (state) => { state.isBudgetPerfLoading = true; });
    builder.addCase(fetchBudgetPerformance.fulfilled, (state, action) => { state.isBudgetPerfLoading = false; state.budgetPerformance = action.payload; });
    builder.addCase(fetchBudgetPerformance.rejected, (state, action) => { state.isBudgetPerfLoading = false; state.error = action.payload as string; });

    // Spending Velocity
    builder.addCase(fetchSpendingVelocity.pending, (state) => { state.isVelocityLoading = true; });
    builder.addCase(fetchSpendingVelocity.fulfilled, (state, action) => { state.isVelocityLoading = false; state.spendingVelocity = action.payload; });
    builder.addCase(fetchSpendingVelocity.rejected, (state, action) => { state.isVelocityLoading = false; state.error = action.payload as string; });

    // Category Treemap
    builder.addCase(fetchCategoryTreemap.pending, (state) => { state.isTreemapLoading = true; });
    builder.addCase(fetchCategoryTreemap.fulfilled, (state, action) => { state.isTreemapLoading = false; state.categoryTreemap = action.payload; });
    builder.addCase(fetchCategoryTreemap.rejected, (state, action) => { state.isTreemapLoading = false; state.error = action.payload as string; });

    // Financial Health
    builder.addCase(fetchFinancialHealth.pending, (state) => { state.isHealthLoading = true; });
    builder.addCase(fetchFinancialHealth.fulfilled, (state, action) => { state.isHealthLoading = false; state.financialHealth = action.payload; });
    builder.addCase(fetchFinancialHealth.rejected, (state, action) => { state.isHealthLoading = false; state.error = action.payload as string; });

    // Expense Calendar
    builder.addCase(fetchExpenseCalendar.pending, (state) => { state.isCalendarLoading = true; });
    builder.addCase(fetchExpenseCalendar.fulfilled, (state, action) => { state.isCalendarLoading = false; state.expenseCalendar = action.payload; });
    builder.addCase(fetchExpenseCalendar.rejected, (state, action) => { state.isCalendarLoading = false; state.error = action.payload as string; });

    // Top Merchants
    builder.addCase(fetchTopMerchants.pending, (state) => { state.isMerchantsLoading = true; });
    builder.addCase(fetchTopMerchants.fulfilled, (state, action) => { state.isMerchantsLoading = false; state.topMerchants = action.payload; });
    builder.addCase(fetchTopMerchants.rejected, (state, action) => { state.isMerchantsLoading = false; state.error = action.payload as string; });

    // Recurring vs Discretionary
    builder.addCase(fetchRecurringVsDiscretionary.pending, (state) => { state.isRecurringLoading = true; });
    builder.addCase(fetchRecurringVsDiscretionary.fulfilled, (state, action) => { state.isRecurringLoading = false; state.recurringVsDiscretionary = action.payload; });
    builder.addCase(fetchRecurringVsDiscretionary.rejected, (state, action) => { state.isRecurringLoading = false; state.error = action.payload as string; });

    // Day of Week
    builder.addCase(fetchDayOfWeek.pending, (state) => { state.isDayOfWeekLoading = true; });
    builder.addCase(fetchDayOfWeek.fulfilled, (state, action) => { state.isDayOfWeekLoading = false; state.dayOfWeek = action.payload; });
    builder.addCase(fetchDayOfWeek.rejected, (state, action) => { state.isDayOfWeekLoading = false; state.error = action.payload as string; });

    // [T114-F4] Items Summary
    builder.addCase(fetchItemsSummary.pending, (state) => { state.isItemsSummaryLoading = true; });
    builder.addCase(fetchItemsSummary.fulfilled, (state, action) => { state.isItemsSummaryLoading = false; state.itemsSummary = action.payload; });
    builder.addCase(fetchItemsSummary.rejected, (state, action) => { state.isItemsSummaryLoading = false; state.error = action.payload as string; });

    // [T114-F4] Items Frequency
    builder.addCase(fetchItemsFrequency.pending, (state) => { state.isItemsFrequencyLoading = true; });
    builder.addCase(fetchItemsFrequency.fulfilled, (state, action) => { state.isItemsFrequencyLoading = false; state.itemsFrequency = action.payload; });
    builder.addCase(fetchItemsFrequency.rejected, (state, action) => { state.isItemsFrequencyLoading = false; state.error = action.payload as string; });

    // [T114-F4] Items Search
    builder.addCase(fetchItemsSearch.pending, (state) => { state.isItemsSearchLoading = true; });
    builder.addCase(fetchItemsSearch.fulfilled, (state, action) => { state.isItemsSearchLoading = false; state.itemsSearch = action.payload; });
    builder.addCase(fetchItemsSearch.rejected, (state, action) => { state.isItemsSearchLoading = false; state.error = action.payload as string; });

    // [T155P2] Projected Balance
    builder.addCase(fetchProjectedBalance.pending, (state) => { state.isProjectedLoading = true; state.error = null; });
    builder.addCase(fetchProjectedBalance.fulfilled, (state, action) => { state.isProjectedLoading = false; state.projectedBalance = action.payload; });
    builder.addCase(fetchProjectedBalance.rejected, (state, action) => { state.isProjectedLoading = false; state.error = action.payload as string; });
  },
});

export const { clearAnalyticsError, clearAnalytics } = analyticsSlice.actions;
export default analyticsSlice.reducer;
